import React, { SyntheticEvent, useState } from 'react';
import _ from 'lodash';

import {
    Box,
    Tabs,
    Tab,
    Typography,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon
} from '@mui/icons-material';
import Chip from '@mui/material/Chip';

import { fetchSettableMetadataForTenant, selectSettableMetadataForTenant } from '@actions/metadata';
import { useDispatch, useSelector } from '@hooks';
import type { FetchComponentPropsType } from '@commonComponents/Fetch/Fetch';
import { TenantProfileLabelKey, TenantProfileType } from '@types';
import { formatDate } from '@utils/parsing';

import SectionCard from './Profile/SectionCard';
import DataRow from './Profile/DataRow';
import ProfileHero from './Profile/ProfileHero';
import Map from './Profile/Map';
import RoutesMap from './Profile/RoutesMap';
import CapabilityChips from './Profile/CapabilityChips';
import RatioBar from './Profile/RatioBar';

function TabPanel({ value, current, children }: { value: string; current: string; children: React.ReactNode }) {
    if (value !== current) return null;
    return (
        <Box role="tabpanel" sx={{ mt: 3 }}>
            {children}
        </Box>
    );
}

const gridSx = {
    display: 'grid',
    gap: 2.5,
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
} as const;

export const ProfileCommon: React.FC<FetchComponentPropsType<TenantProfileType>> = (props) => {
    const { entity } = props;
    const [mapOpen, setMapOpen] = useState(false);

    const dispatch = useDispatch();

    const settableMetadata = useSelector(selectSettableMetadataForTenant());

    const {
        activeSubscription,
        allabolagUrl,
        billingCity,
        billingEmail,
        billingMark,
        billingStreet,
        billingZip,
        cashRatio,
        city,
        companyStatus,
        customLicencesCertificates,
        description,
        email,
        employerContributions,
        equipment,
        equityRatio,
        fTax,
        fairtransportDescription,
        fairtransportMembership,
        fairtransportPhone,
        fairtransportEmail,
        fairtransportUrl,
        industries,
        isAccepted,
        isCarrier,
        isPending,
        isPublic,
        isRejected,
        lat,
        legalEntity,
        licencesCertificates,
        lng,
        name,
        netResult,
        orgNumber,
        phone,
        profitAfterFinancialItems,
        profitMargin,
        scrapedAt,
        sniIndustries,
        street,
        sustainability,
        vat,
        vehicleTypes,
        website,
        zip,
        revenue,
        slug,
        numberOfTrucks,
        logoUrl,
        routes = [],
        recommendedByCurrentTenant = false
    } = entity;

    const heroProps = {
        activeSubscription,
        allabolagUrl,
        logoUrl,
        city,
        email: email || fairtransportEmail,
        isAccepted,
        isCarrier,
        isPending,
        isRejected,
        isPublic: isPublic || false,
        fairtransportMembership,
        fairtransportUrl,
        name,
        orgNumber,
        phone: phone || fairtransportPhone,
        street,
        website,
        zip,
        slug,
        revenue,
        numberOfTrucks,
        recommendedByCurrentTenant,
        canOpenMap: Boolean(lat && lng),
        onOpenMap: () => setMapOpen(true)
    };

    const equipmentCapabilities = settableMetadata.equipment
        .map((e) => ({ label: e, available: equipment.includes(e) }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const vehicleTypesCapabilities = settableMetadata.vehicleTypes
        .map((e) => ({ label: e, available: vehicleTypes.includes(e) }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const adrClasses = licencesCertificates.filter((c) => c.startsWith('ADR Klass')).map((c) => c.replace('ADR Klass', ''));

    const allCertificates = [...licencesCertificates.filter((c) => !c.startsWith('ADR Klass')), ...customLicencesCertificates];

    const sustainabilityCapabilities = settableMetadata.sustainability
        .map((e) => ({ label: e, available: sustainability.includes(e) }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const about = description || fairtransportDescription || 'Ingen beskrivning tillgänglig';

    const formattedScrapedDate = formatDate(scrapedAt);
    const scrapedDateSubtitle = formattedScrapedDate !== '-' ? `uppdaterat ${formattedScrapedDate}` : undefined;

    const economy = {
        scrapedAt: formatDate(scrapedAt),
        profitAndTaxes: [
            {
                field: 'netResult' as TenantProfileLabelKey,
                value: netResult
            },
            {
                field: 'profitAfterFinancialItems' as TenantProfileLabelKey,
                value: profitAfterFinancialItems
            },
            {
                field: 'vat' as TenantProfileLabelKey,
                value: vat
            },
            {
                field: 'fTax' as TenantProfileLabelKey,
                value: fTax
            },
            {
                field: 'employerContributions' as TenantProfileLabelKey,
                value: employerContributions
            }
        ].filter((pt) => pt.value !== null && pt.value !== undefined),
        ratios: [
            {
                field: 'profitMargin' as TenantProfileLabelKey,
                value: profitMargin
            },
            {
                field: 'equityRatio' as TenantProfileLabelKey,
                value: equityRatio
            },
            {
                field: 'cashRatio' as TenantProfileLabelKey,
                value: cashRatio
            },
            {
                field: 'netResult' as TenantProfileLabelKey,
                value: netResult !== null && revenue !== null
                    ? Math.round((netResult / revenue) * 100)
                    : null,
                hint: 'som procent av omsättning'
            },
            {
                field: 'profitAfterFinancialItems' as TenantProfileLabelKey,
                value: profitAfterFinancialItems !== null && revenue !== null
                    ? Math.round((profitAfterFinancialItems / revenue) * 100)
                    : null,
                hint: 'som procent av omsättning'
            }
        ].filter(
            (r): r is { field: TenantProfileLabelKey; value: number; hint?: string } => r.value !== null && r.value !== undefined
        )
    };

    const fullBillingAddress = [
        _.capitalize(billingStreet),
        billingZip,
        billingCity
    ].filter(Boolean).join('\n');

    const fullAddress = [
        _.capitalize(street),
        zip,
        city
    ].filter(Boolean).join('\n');

    const billing = [
        { field: 'fullBillingAddress' as TenantProfileLabelKey, value: fullBillingAddress },
        { field: 'billingEmail' as TenantProfileLabelKey, value: billingEmail, href: `mailto:${billingEmail}` },
        { field: 'billingMark' as TenantProfileLabelKey, value: billingMark }
    ].filter((m) => Boolean(m.value));

    const [tab, setTab] = useState('oversikt');
    const handleChange = (_e: SyntheticEvent, v: string) => setTab(v);

    React.useEffect(() => {
        dispatch(fetchSettableMetadataForTenant());
    }, [dispatch]);

    return (
        <>
            <ProfileHero {...heroProps} />
            <Dialog
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pr: 1 }}>
                    <Box>
                        <Typography
                            component="span"
                            sx={{ display: 'block', fontWeight: 600 }}
                        >
                            {name}
                        </Typography>
                        {fullAddress && (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                {fullAddress.replace(/\n/g, ', ')}
                            </Typography>
                        )}
                    </Box>
                    <IconButton aria-label="Stäng" onClick={() => setMapOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ height: 400, p: 0, pt: 0, overflow: 'hidden' }}>
                    <Map lat={lat} lng={lng} />
                </DialogContent>
            </Dialog>
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
            >
                <Tabs
                    value={tab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="primary"
                    indicatorColor="primary"
                >
                    <Tab value="oversikt" label="Om" />
                    <Tab value="ekonomi" sx={{ display: isCarrier ? undefined : 'none' }} label="Ekonomi" />
                    <Tab value="kapacitet" sx={{ display: isCarrier ? undefined : 'none' }} label="Fordon" />
                    <Tab
                        value="tackning"
                        sx={{ display: isCarrier && routes.length > 0 ? undefined : 'none' }}
                        label="Täckning"
                    />
                    <Tab value="kompetens" sx={{ display: isCarrier ? undefined : 'none' }} label="Certifikat" />
                </Tabs>
            </Box>
            <Box>
                <TabPanel value="oversikt" current={tab}>
                    <Box sx={gridSx}>
                        <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                            <SectionCard title="Om företaget">
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: 16 }}>
                                    {about}
                                </Typography>
                            </SectionCard>
                        </Box>
                        <SectionCard title="Företagsuppgifter">
                            <DataRow field="legalEntity" value={legalEntity} display={legalEntity ? 'flex' : 'none'} />
                            <DataRow field="companyStatus" value={companyStatus} display={companyStatus ? 'flex' : 'none'} />
                            <DataRow field="orgNumber" value={orgNumber} />
                            <DataRow field="fullAddress" value={fullAddress} display={fullAddress ? 'flex' : 'none'} />
                        </SectionCard>
                        {billing.length > 0 && (
                            <SectionCard title="Faktureringsuppgifter">
                                {billing.map((m) => (
                                    <DataRow key={m.field} field={m.field} value={m.value} href={m.href} />
                                ))}
                            </SectionCard>
                        )}
                    </Box>
                </TabPanel>
                <TabPanel value="ekonomi" current={tab}>
                    <Box sx={gridSx}>
                        {economy.ratios.length > 0 && (
                            <SectionCard title="Nyckeltal" subtitle={scrapedDateSubtitle}>
                                {economy.ratios
                                    .map((r) => (
                                        <RatioBar key={r.field} ratio={r} />
                                    ))}
                            </SectionCard>
                        )}
                        {economy.profitAndTaxes.length > 0 && (
                            <SectionCard title="Resultat & skatt" subtitle={scrapedDateSubtitle}>
                                {economy.profitAndTaxes
                                    .map((m) => (
                                        <DataRow key={m.field} field={m.field} value={m.value} />
                                    ))}
                            </SectionCard>
                        )}
                    </Box>
                </TabPanel>
                <TabPanel value="kapacitet" current={tab}>
                    <Box sx={gridSx}>
                        <SectionCard title="Fordon" tail={numberOfTrucks != null ? `${numberOfTrucks} st` : undefined}>
                            <CapabilityChips items={vehicleTypesCapabilities} />
                        </SectionCard>
                        <SectionCard title="Utrustning">
                            <CapabilityChips items={equipmentCapabilities} />
                        </SectionCard>
                    </Box>
                </TabPanel>
                <TabPanel value="tackning" current={tab}>
                    <Box sx={gridSx}>
                        <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                            <SectionCard title="Täckning och rutter">
                                <RoutesMap routes={routes} />
                            </SectionCard>
                        </Box>
                    </Box>
                </TabPanel>
                <TabPanel value="kompetens" current={tab}>
                    <Box sx={gridSx}>
                        {adrClasses.length > 0 && (
                            <SectionCard title="ADR-klasser">
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Godkänd för transport av farligt gods i följande ADR-klasser:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {adrClasses.map((n) => (
                                        <Box
                                            key={n}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 99999,
                                                bgcolor: 'success.main',
                                                color: 'primary.contrastText',
                                                fontWeight: 600,
                                                fontSize: 14
                                            }}
                                        >
                                            {n}
                                        </Box>
                                    ))}
                                </Box>
                            </SectionCard>
                        )}
                        {allCertificates.length > 0 && (
                            <SectionCard title="Certifikat">
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    <CapabilityChips items={allCertificates.map((c) => ({ label: c, available: true }))} />
                                </Box>
                            </SectionCard>
                        )}
                        <SectionCard title="Hållbarhet">
                            <CapabilityChips items={sustainabilityCapabilities} />
                        </SectionCard>
                        {(industries.length + sniIndustries.length) > 0 && (
                            <SectionCard title="Branscher & SNI-koder">
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {industries.map((industry) => (
                                        <Chip key={`industry-${industry}`} label={industry} variant="outlined" />
                                    ))}
                                    {industries.length > 0 && sniIndustries.length > 0 && (
                                        <Box sx={{ flexBasis: '100%' }} />
                                    )}
                                    {sniIndustries.map((sniIndustry) => (
                                        <Chip key={`sni-${sniIndustry}`} label={sniIndustry} variant="outlined" />
                                    ))}
                                </Box>
                            </SectionCard>
                        )}
                    </Box>
                </TabPanel>
            </Box>
        </>
    );
};

export default ProfileCommon;
